import React, { useEffect, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, X, Trash2, List } from "lucide-react";
import { apiFetch } from "../../api/api";
import { AxiosError } from "axios";

const QuestionsList = () => {
  const [data, setData] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  // 🧩 state برای افزودن سؤال
  const [newQuestion, setNewQuestion] = useState({
    title: "",
    type: 1,
    sid: "",
    status: 1,
  });

  // 🧩 state برای مدیریت گزینه‌ها
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
    null
  );
  const [newOptionTitle, setNewOptionTitle] = useState<string>("");
  const [optionsList, setOptionsList] = useState<any[]>([]);

  // 🧩 state برای حذف
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  // 📦 گرفتن لیست سوالات
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await apiFetch({
        data: { params: [null, null, null, null, null, 4, 0, null] },
      });

      if (res.success && Array.isArray(res.rows)) {
        const formatted = res.rows.map((item: any) => ({
          id: item.QID,
          title: item.Title,
          type: item.QType,
          sid: item.SID,
          isActive: item.Status?.data?.[0] === 1,
        }));
        setData(formatted);
      } else {
        setError("خطا در دریافت اطلاعات");
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError.message);
    } finally {
      setLoading(false);
    }
  };

  // 📘 گرفتن لیست موضوعات
  const fetchSections = async () => {
    try {
      const res = await apiFetch({
        data: { params: [null, null, 4, 1, null] },
      });
      if (res.success && Array.isArray(res.rows)) {
        setSections(res.rows);
      }
    } catch (error) {
      console.error("خطا در دریافت موضوع ها:", error);
    }
  };

  // 🧩 گرفتن گزینه‌های یک سؤال
  const fetchOptions = async (qid: number) => {
    try {
      const res = await apiFetch({
        data: { params: [null, qid, null, null, 4, 0, null] },
      });
      if (res.success && Array.isArray(res.rows)) {
        setOptionsList(res.rows);
      } else {
        setOptionsList([]);
      }
    } catch (error) {
      console.error("خطا در دریافت گزینه‌ها:", error);
      setOptionsList([]);
    }
  };

  useEffect(() => {
    fetchQuestions();
    fetchSections();
  }, []);

  // 🔍 جستجو
  const handleSearch = (e: any) => {
    const search = e.target.value.toLowerCase();
    if (!search) return fetchQuestions();
    const filtered = data.filter((q) =>
      q.title.toLowerCase().includes(search)
    );
    setData(filtered);
  };

  // ➕ افزودن سؤال جدید
  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.title.trim()) return alert("عنوان سوال را وارد کنید");
    if (!newQuestion.sid) return alert("لطفاً موضوع را انتخاب کنید");

    try {
      setLoading(true);
      const res = await apiFetch({
        data: {
          params: [
            null,
            newQuestion.title,
            newQuestion.type,
            Number(newQuestion.sid),
            newQuestion.status,
            1,
            0,
              null
          ],
        },
      });

      if (res.success) {
        alert("سؤال با موفقیت ثبت شد ✅");
        setShowModal(false);
        setNewQuestion({ title: "", type: 1, sid: "", status: 1 });
        fetchQuestions();
      } else {
        alert("خطا در ثبت سؤال ❌");
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      alert("خطا در ارتباط با سرور: " + axiosError.message);
    } finally {
      setLoading(false);
    }
  };

  // 🗑 حذف سؤال
  const handleDeleteQuestion = async () => {
    if (!deleteTargetId) return;
    try {
      const res = await apiFetch({
        data: { params: [deleteTargetId, null, null, null, null, 3, 0, null] },
      });
      if (res.success) {
        alert("سؤال حذف شد ✅");
        fetchQuestions();
      } else {
        alert("خطا در حذف سؤال ❌");
      }
    } catch (error) {
      alert("خطا در حذف سؤال");
    } finally {
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    }
  };

  // ➕ افزودن گزینه
  const handleAddOption = async () => {
    if (!newOptionTitle.trim() || !selectedQuestionId)
      return alert("عنوان گزینه را وارد کنید");

    try {
      const res = await apiFetch({
        data: {
          params: [null, selectedQuestionId, newOptionTitle, null, 1, 0, null],
        },
      });

      if (res.success) {
        setNewOptionTitle("");
        fetchOptions(selectedQuestionId);
      } else {
        alert("خطا در افزودن گزینه ❌");
      }
    } catch (error) {
      alert("خطا در ارتباط با سرور");
    }
  };

  // 🌀 وضعیت لودینگ
  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="w-12 h-12 border-4 border-custom-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-slate-600 dark:text-zink-200">
          در حال بارگذاری داده‌ها...
        </p>
      </div>
    );

  // ❌ خطا
  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-red-500">
        <p>{error}</p>
        <button
          onClick={fetchQuestions}
          className="mt-3 btn bg-custom-500 text-white px-4 py-2 rounded-lg hover:bg-custom-600"
        >
          تلاش مجدد
        </button>
      </div>
    );

  return (
    <React.Fragment>
      <BreadCrumb title="لیست سوالات" pageTitle="Questions" />

      {/* 🔍 فیلتر و افزودن */}
      <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2">
        <div>
          <input
            type="text"
            placeholder="جستجو بر اساس عنوان..."
            onChange={handleSearch}
            className="form-input w-full border-slate-200 dark:border-zink-500 focus:border-custom-500 dark:focus:border-custom-800 dark:bg-zink-700"
          />
        </div>
        <div className="text-left lg:text-right">
          <button
            onClick={() => setShowModal(true)}
            className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
          >
            <Plus className="inline-block size-4" /> <span>افزودن سوال</span>
          </button>
        </div>
      </div>

      {/* 📋 جدول سوالات */}
      <div className="card">
        <div className="card-body overflow-x-auto">
          {data && data.length > 0 ? (
            <table className="w-full whitespace-nowrap text-sm border border-slate-200 dark:border-zink-600">
              <thead className="bg-slate-100 dark:bg-zink-700 text-slate-600 dark:text-zink-200">
                <tr>
                  <th className="px-4 py-2 text-right">#</th>
                  <th className="px-4 py-2 text-right">عنوان</th>
                  <th className="px-4 py-2 text-right">نوع</th>
                  <th className="px-4 py-2 text-right">موضوع</th>
                  <th className="px-4 py-2 text-right">وضعیت</th>
                  <th className="px-4 py-2 text-right">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, i) => (
                  <tr
                    key={item.id}
                    className="border-t border-slate-200 dark:border-zink-600 hover:bg-slate-50 dark:hover:bg-zink-800"
                  >
                    <td className="px-4 py-2">{i + 1}</td>
                    <td className="px-4 py-2">{item.title}</td>
                    <td className="px-4 py-2">
                      {item.type === 1 ? "چند گزینه‌ای" : "تشریحی"}
                    </td>
                    <td className="px-4 py-2">{item.sid}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-1 text-xs rounded ${item.isActive
                            ? "bg-green-100 text-green-600"
                            : "bg-gray-200 text-gray-600"
                          }`}
                      >
                        {item.isActive ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td className="px-4 py-2 flex gap-2">

                      <button
                        disabled={item.type !== 1}
                        className={`btn-sm rounded px-2 py-1 flex items-center gap-1
                         ${item.type === 1
                            ? "bg-blue-500 hover:bg-blue-600 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }
                                `}
                        onClick={() => {
                          if (item.type !== 1) return;

                          setSelectedQuestionId(item.id);
                          setShowOptionsModal(true);
                          fetchOptions(item.id);
                        }}
                      >
                        <List size={14} /> گزینه‌ها
                      </button>
                      <button
                        className="btn-sm bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 flex items-center gap-1"
                        onClick={() => {
                          setDeleteTargetId(item.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 size={14} /> حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-slate-500 py-6">
              هیچ سوالی یافت نشد.
            </p>
          )}
        </div>
      </div>

      {/* 🧩 مدال افزودن سؤال */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">افزودن سؤال جدید</h3>
              <button onClick={() => setShowModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            <form onSubmit={handleAddQuestion} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">عنوان سؤال</label>
                <input
                  type="text"
                  value={newQuestion.title}
                  onChange={(e) =>
                    setNewQuestion({ ...newQuestion, title: e.target.value })
                  }
                  className="form-input w-full"
                  placeholder="مثلاً: از پزشک خود راضی هستید؟"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">نوع سؤال</label>
                <select
                  value={newQuestion.type}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      type: Number(e.target.value),
                    })
                  }
                  className="form-input w-full"
                >
                  <option value={1}>چند گزینه‌ای</option>
                  <option value={2}>تشریحی</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1">موضوع</label>
                <select
                  value={newQuestion.sid}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      sid: e.target.value,
                    })
                  }
                  className="form-input w-full"
                >
                  <option value="">انتخاب کنید...</option>
                  {sections.map((sec) => (
                    <option key={sec.SID} value={sec.SID}>
                      {sec.Title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn border border-slate-300"
                >
                  لغو
                </button>
                <button
                  type="submit"
                  className="btn bg-custom-500 text-white hover:bg-custom-600"
                >
                  ثبت سؤال
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ⚙️ مدال گزینه‌ها */}
      {showOptionsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">مدیریت گزینه‌ها</h3>
              <button onClick={() => setShowOptionsModal(false)}>
                <X className="text-slate-600 hover:text-red-500" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="عنوان گزینه جدید..."
                value={newOptionTitle}
                onChange={(e) => setNewOptionTitle(e.target.value)}
                className="form-input flex-1"
              />
              <button
                onClick={handleAddOption}
                className="btn bg-custom-500 text-white hover:bg-custom-600"
              >
                افزودن
              </button>
            </div>

            <ul className="border-t border-slate-200 dark:border-zink-700 mt-3 pt-3 space-y-2 max-h-60 overflow-y-auto">
              {optionsList.length > 0 ? (
                optionsList.map((opt, i) => (
                  <li
                    key={i}
                    className="bg-slate-50 dark:bg-zink-700 px-3 py-2 rounded"
                  >
                    {opt.Title}
                  </li>
                ))
              ) : (
                <p className="text-center text-slate-500">هیچ گزینه‌ای ثبت نشده</p>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* 🗑 مدال حذف */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-sm p-6 text-center">
            <h3 className="text-lg font-semibold mb-3">
              آیا از حذف این سؤال مطمئن هستید؟
            </h3>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn border border-slate-300"
              >
                خیر
              </button>
              <button
                onClick={handleDeleteQuestion}
                className="btn bg-red-500 text-white hover:bg-red-600"
              >
                بله، حذف کن
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default QuestionsList;


// import React, { useEffect, useState } from "react";
// import BreadCrumb from "Common/BreadCrumb";
// import { Plus, X } from "lucide-react";
// import { apiFetch } from "../../api/api";
// import { AxiosError } from "axios";

// const QuestionsList = () => {
//   const [data, setData] = useState<any[]>([]);
//   const [sections, setSections] = useState<any[]>([]);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [error, setError] = useState<string | null>(null);
//   const [showModal, setShowModal] = useState<boolean>(false);

//   const [newQuestion, setNewQuestion] = useState({
//     title: "",
//     type: 1,
//     sid: "",
//     status: 1,
//   });

//   // 📦 گرفتن لیست سوالات
//   const fetchQuestions = async () => {
//     try {
//       setLoading(true);
//       const res = await apiFetch({
//         data: { params: [null, null, null, null, null, 4, 0] },
//       });

//       if (res.success && Array.isArray(res.rows)) {
//         const formatted = res.rows.map((item: any) => ({
//           id: item.QID,
//           title: item.Title,
//           type: item.QType,
//           sid: item.SID,
//           isActive: item.Status?.data?.[0] === 1,
//         }));
//         setData(formatted);
//       } else {
//         setError("خطا در دریافت اطلاعات");
//       }
//     } catch (error) {
//       const axiosError = error as AxiosError;
//       setError(axiosError.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 📘 گرفتن لیست موضوع (SID)
//   const fetchSections = async () => {
//     try {
//       const res = await apiFetch({
//         data: { params: [null, null, 4, 1] },
//       });
//       if (res.success && Array.isArray(res.rows)) {
//         setSections(res.rows);
//       }
//     } catch (error) {
//       console.error("خطا در دریافت موضوع ها:", error);
//     }
//   };

//   useEffect(() => {
//     fetchQuestions();
//     fetchSections();
//   }, []);

//   // 🔍 جستجو
//   const handleSearch = (e: any) => {
//     const search = e.target.value.toLowerCase();
//     if (!search) return fetchQuestions();
//     const filtered = data.filter((q) =>
//       q.title.toLowerCase().includes(search)
//     );
//     setData(filtered);
//   };

//   // ➕ افزودن سؤال جدید
//   const handleAddQuestion = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!newQuestion.title.trim()) return alert("عنوان سوال را وارد کنید");
//     if (!newQuestion.sid) return alert("لطفاً موضوع را انتخاب کنید");

//     try {
//       setLoading(true);
//       const res = await apiFetch({
//         data: {
//           params: [
//             null,
//             newQuestion.title,
//             newQuestion.type,
//             Number(newQuestion.sid),
//             newQuestion.status,
//             1,
//             0,
//           ],
//         },
//       });

//       if (res.success) {
//         alert("سؤال با موفقیت ثبت شد ✅");
//         setShowModal(false);
//         setNewQuestion({ title: "", type: 1, sid: "", status: 1 });
//         fetchQuestions();
//       } else {
//         alert("خطا در ثبت سؤال ❌");
//       }
//     } catch (error) {
//       const axiosError = error as AxiosError;
//       alert("خطا در ارتباط با سرور: " + axiosError.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 🌀 وضعیت لودینگ
//   if (loading)
//     return (
//       <div className="flex flex-col items-center justify-center min-h-[70vh]">
//         <div className="w-12 h-12 border-4 border-custom-500 border-t-transparent rounded-full animate-spin"></div>
//         <p className="mt-4 text-slate-600 dark:text-zink-200">
//           در حال بارگذاری داده‌ها...
//         </p>
//       </div>
//     );

//   // ❌ خطا در دریافت داده‌ها
//   if (error)
//     return (
//       <div className="flex flex-col items-center justify-center min-h-[70vh] text-red-500">
//         <p>{error}</p>
//         <button
//           onClick={fetchQuestions}
//           className="mt-3 btn bg-custom-500 text-white px-4 py-2 rounded-lg hover:bg-custom-600"
//         >
//           تلاش مجدد
//         </button>
//       </div>
//     );

//   return (
//     <React.Fragment>
//       <BreadCrumb title="لیست سوالات" pageTitle="Questions" />

//       {/* 🔍 فیلتر و افزودن */}
//       <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2">
//         <div>
//           <input
//             type="text"
//             placeholder="جستجو بر اساس عنوان..."
//             onChange={handleSearch}
//             className="form-input w-full border-slate-200 dark:border-zink-500 focus:border-custom-500 dark:focus:border-custom-800 dark:bg-zink-700"
//           />
//         </div>
//         <div className="text-left lg:text-right">
//           <button
//             onClick={() => setShowModal(true)}
//             className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
//           >
//             <Plus className="inline-block size-4" /> <span>افزودن سوال</span>
//           </button>
//         </div>
//       </div>

//       {/* 📋 جدول سوالات */}
//       <div className="card">
//         <div className="card-body overflow-x-auto">
//           {data && data.length > 0 ? (
//             <table className="w-full whitespace-nowrap text-sm border border-slate-200 dark:border-zink-600">
//               <thead className="bg-slate-100 dark:bg-zink-700 text-slate-600 dark:text-zink-200">
//                 <tr>
//                   <th className="px-4 py-2 text-right">#</th>
//                   <th className="px-4 py-2 text-right">عنوان</th>
//                   <th className="px-4 py-2 text-right">نوع سوال</th>
//                   <th className="px-4 py-2 text-right">موضوع سوال</th>
//                   <th className="px-4 py-2 text-right">وضعیت</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {data.map((item, i) => (
//                   <tr
//                     key={item.id}
//                     className="border-t border-slate-200 dark:border-zink-600 hover:bg-slate-50 dark:hover:bg-zink-800"
//                   >
//                     <td className="px-4 py-2">{i + 1}</td>
//                     <td className="px-4 py-2">{item.title}</td>
//                     <td className="px-4 py-2">
//                       {item.type === 1 ? "چند گزینه‌ای" : "تشریحی"}
//                     </td>
//                     <td className="px-4 py-2">{item.sid}</td>
//                     <td className="px-4 py-2">
//                       <span
//                         className={`px-2 py-1 text-xs rounded ${
//                           item.isActive
//                             ? "bg-green-100 text-green-600"
//                             : "bg-gray-200 text-gray-600"
//                         }`}
//                       >
//                         {item.isActive ? "فعال" : "غیرفعال"}
//                       </span>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           ) : (
//             <p className="text-center text-slate-500 py-6">
//               هیچ سوالی یافت نشد.
//             </p>
//           )}
//         </div>
//       </div>

//       {/* 🧩 مدال افزودن سؤال جدید */}
//       {showModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
//           <div className="bg-white dark:bg-zink-800 rounded-lg shadow-lg w-full max-w-md p-6">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
//                 افزودن سؤال جدید
//               </h3>
//               <button onClick={() => setShowModal(false)}>
//                 <X className="text-slate-600 hover:text-red-500" />
//               </button>
//             </div>

//             <form onSubmit={handleAddQuestion} className="space-y-4">
//               {/* عنوان */}
//               <div>
//                 <label className="block text-sm mb-1">عنوان سؤال</label>
//                 <input
//                   type="text"
//                   value={newQuestion.title}
//                   onChange={(e) =>
//                     setNewQuestion({ ...newQuestion, title: e.target.value })
//                   }
//                   className="form-input w-full border-slate-300 dark:bg-zink-700 dark:border-zink-600 focus:border-custom-500"
//                   placeholder="مثلاً: از پزشک خود راضی هستید؟"
//                 />
//               </div>

//               {/* نوع سوال */}
//               <div>
//                 <label className="block text-sm mb-1">نوع سؤال</label>
//                 <select
//                   value={newQuestion.type}
//                   onChange={(e) =>
//                     setNewQuestion({
//                       ...newQuestion,
//                       type: Number(e.target.value),
//                     })
//                   }
//                   className="form-input w-full border-slate-300 dark:bg-zink-700 dark:border-zink-600 focus:border-custom-500"
//                 >
//                   <option value={1}>چند گزینه‌ای</option>
//                   <option value={2}>تشریحی</option>
//                 </select>
//               </div>

//               {/* موضوع (SID) */}
//               <div>
//                 <label className="block text-sm mb-1">انتخاب موضوع</label>
//                 <select
//                   value={newQuestion.sid}
//                   onChange={(e) =>
//                     setNewQuestion({
//                       ...newQuestion,
//                       sid: e.target.value,
//                     })
//                   }
//                   className="form-input w-full border-slate-300 dark:bg-zink-700 dark:border-zink-600 focus:border-custom-500"
//                 >
//                   <option value="">انتخاب کنید...</option>
//                   {sections.map((sec) => (
//                     <option key={sec.SID} value={sec.SID}>
//                       {sec.Title}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* دکمه‌ها */}
//               <div className="flex justify-end gap-2">
//                 <button
//                   type="button"
//                   onClick={() => setShowModal(false)}
//                   className="btn border border-slate-300 dark:border-zink-600 text-slate-600 hover:bg-slate-100"
//                 >
//                   لغو
//                 </button>
//                 <button
//                   type="submit"
//                   className="btn bg-custom-500 text-white hover:bg-custom-600"
//                 >
//                   ثبت سؤال
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </React.Fragment>
//   );
// };

// export default QuestionsList;
